import { useEffect, useState, useMemo } from "react";
import { useFormikContext } from "formik";
import { Box, Stack, Typography, ThemeProvider } from "@mui/material";
import { Editor, Frame, Element } from "@craftjs/core";
import lz from "lzutf8";
import useOrganizationTheme from "@/features/organizations/useOrganizationTheme";
import { OrganizationProvider } from "@/features/websites/components/OrganizationProvider";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { useTerminology } from "@/hooks";

// Import all resolvers used in website editor
import FormBlock from "@/components/editor/blocks/FormBlock";
import TextInputBlock from "@/components/editor/blocks/TextInputBlock";
import NumberInputBlock from "@/components/editor/blocks/NumberInputBlock";
import DateInputBlock from "@/components/editor/blocks/DateInputBlock";
import SectionBlock from "@/components/editor/blocks/section-block/SectionBlock";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import TextBlock from "@/components/editor/blocks/TextBlock";
import ImageBlock from "@/components/editor/blocks/ImageBlock";
import CarouselBlock from "@/components/editor/blocks/CarouselBlock";
import VerticalSpacerBlock from "@/components/editor/blocks/VerticalSpacerBlock";
import DishBlock from "@/components/editor/blocks/DishBlock";
import DropRegionBlock from "@/components/editor/blocks/DropRegionBlock";
import ButtonBlock from "@/components/editor/blocks/ButtonBlock";
import SocialMediaBlock from "@/components/editor/blocks/SocialMediaBlock";
import MapBlock from "@/components/editor/blocks/MapBlock";
import FaqsBlock from "@/components/editor/blocks/FaqsBlock";
import MenuBlock from "@/components/editor/blocks/MenuBlock";
import CartBlock from "@/components/editor/blocks/CartBlock";
import RegionBlock from "@/components/editor/blocks/RegionBlock";
import RepeatBlock from "@/components/editor/blocks/RepeatBlock";

interface Props {
  website: any;
  activeTemplate: any;
}

const PreviewMockup = ({ website, activeTemplate }: Props) => {
  const { values } = useFormikContext<any>();
  const { t } = useTerminology();
  const [json, setJson] = useState<string | null>(null);
  const organizationTheme = useOrganizationTheme(values.organization);

  // Decompress JSON data from database website/template layout
  useEffect(() => {
    const rawData = activeTemplate?.data || website?.data;
    if (rawData) {
      try {
        const decompressed = lz.decompress(lz.decodeBase64(rawData));
        setJson(decompressed);
      } catch (err) {
        console.error("Failed to decompress layout data:", err);
      }
    } else {
      setJson(null);
    }
  }, [activeTemplate?.data, website?.data]);

  const styles = activeTemplate?.styles ?? website?.styles ?? "";

  const processedStyles = useMemo(() => {
    if (!styles) return "";
    return styles.replace(/@media\s*\(/g, "@container (");
  }, [styles]);

  if (!json) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.02)",
          borderRadius: 3,
          border: "1px dashed rgba(0,0,0,0.12)",
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Cargando vista previa...
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%", height: "100%" }}>

      {/* Styled Smartphone Mockup */}
      <Box
        sx={{
          position: "relative",
          width: "500px",
          height: "800px",
          borderRadius: "36px",
          border: "12px solid #1a1a1a",
          boxShadow: "0px 16px 40px rgba(0, 0, 0, 0.15)",
          bgcolor: "white",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Notch / Dynamic Island */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "110px",
            height: "20px",
            bgcolor: "#1a1a1a",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            zIndex: 100,
          }}
        />

        {/* Scrollable mockup content area */}
        <Box
          id="main-container"
          className="viewport"
          sx={{
            flex: 1,
            overflowY: "auto",
            position: "relative",
            width: "100%",
            height: "100%",
            containerType: "inline-size",
            // Custom thin scrollbars for phone preview
            "::-webkit-scrollbar": {
              width: "4px",
            },
            "::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.02)",
            },
            "::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.15)",
              borderRadius: "4px",
            },
          }}
        >
          {/* Inject website styling rules */}
          <style dangerouslySetInnerHTML={{ __html: processedStyles }} />

          {/* Context Providers supplying form values to mustache templates in blocks */}
          <OrganizationProvider.Provider
            value={{
              ...values.organization,
              activeWebsite: {
                ...website,
                settings: values?.settings || activeTemplate?.settings || {},
                metaData: values?.metaData || activeTemplate?.metaData || {},
              },
            }}
          >
            <WebsiteContext.Provider
              value={{
                organization: values.organization,
                website: {
                  ...website,
                  settings: values?.settings || activeTemplate?.settings || {},
                  metaData: values?.metaData || activeTemplate?.metaData || {},
                },
                template: activeTemplate?.id ? { id: activeTemplate?.id } as any : null,
                theme: organizationTheme,
                stablishment: t('establishment'),
                product: t('product'),
                catalog: t('catalog'),
              }}
            >
              <ThemeProvider theme={organizationTheme}>
                <Editor
                  key={JSON.stringify({
                    settings: values?.settings || activeTemplate?.settings || website?.settings || {},
                    organization: values.organization,
                    templateId: activeTemplate?.id || website?.template || "",
                    layout: json
                  })}
                  enabled={false}
                  resolver={{
                    FormBlock,
                    TextInputBlock,
                    NumberInputBlock,
                    DateInputBlock,
                    SectionBlock,
                    ContainerBlock,
                    TextBlock,
                    ImageBlock,
                    CarouselBlock,
                    VerticalSpacerBlock,
                    DishBlock,
                    DropRegionBlock,
                    ButtonBlock,
                    SocialMediaBlock,
                    MapBlock,
                    FaqsBlock,
                    MenuBlock,
                    CartBlock,
                    RegionBlock,
                    RepeatBlock,
                  }}
                >
                  <Frame data={json}>
                    <Element canvas is={ContainerBlock} custom={{ displayName: "App" }}></Element>
                  </Frame>
                </Editor>
              </ThemeProvider>
            </WebsiteContext.Provider>
          </OrganizationProvider.Provider>
        </Box>
      </Box>
    </Stack>
  );
};

export default PreviewMockup;
